import React, { useState, useEffect } from 'react';
import { Alert, Form, Table, Button, Card, FilterGrid } from '@/components/ui';
import ReportParametersCard from '../components/ReportParametersCard';
import { areasService, ServiceArea } from '../../../services/areasService';
import activityService from '../../../services/activityService';
import { Activity } from '../../../types/Activity';
import reportService from '../../../services/reportService';
import Toast from '../../../components/Toast';

interface QBSActualDetailsReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

interface ActivityRow {
  activity: Activity;
  type: string;
  monthValues: (number | string)[]; // M1-M12 values
  valueToDate: number | string;
  costsToDate: number | string;
  profit: number | string; // Gross Margin
  grossMarginPercent: number | string; // GM%
  totalAccomplishment: number | string;
  costPerAccomplish: number | string;
  sched2Rate: number | string;
  avgActualValuePerAccomplishment: number | string;
  grossMarginPerAccomplishment: number | string;
}

const QBSActualDetailsReportView: React.FC<QBSActualDetailsReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    reportingMonth: '', // Can stay empty
    quantifiedYear: '',
    startDate: '',
    endDate: ''
  });
  const [qbsPlanYearOptions, setQbsPlanYearOptions] = useState<number[]>([]);
  const [loadingQbsPlanYears, setLoadingQbsPlanYears] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activityRows, setActivityRows] = useState<ActivityRow[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [monthHeaders, setMonthHeaders] = useState<Array<{ label: string; dateRange: string }>>([]);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);

  const currentYear = new Date().getFullYear();

  // Update service area filter when defaultServiceAreaId changes
  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters(prev => ({ ...prev, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  useEffect(() => {
    if (!filters.serviceArea) {
      setQbsPlanYearOptions([]);
      setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
      return;
    }
    let cancelled = false;
    const serviceAreaId = parseInt(filters.serviceArea, 10);
    if (Number.isNaN(serviceAreaId)) {
      setQbsPlanYearOptions([]);
      return;
    }
    setLoadingQbsPlanYears(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (cancelled) return;
        const eligiblePlans = plans.filter((p) => p.status === 'Open' || p.status === 'Closed');
        const years = Array.from(
          new Set(
            eligiblePlans.map((p) => p.year)
          )
        ).sort((a, b) => b - a);
        setQbsPlanYearOptions(years);
        const openYears = plans.filter((p) => p.status === 'Open').map((p) => p.year);
        let defaultYear: number | null = null;

        if (years.includes(currentYear)) {
          defaultYear = currentYear;
        } else if (openYears.length > 0) {
          defaultYear = Math.max(...openYears);
        } else if (years.length > 0) {
          defaultYear = Math.max(...years);
        }

        setFilters((prev) => ({
          ...prev,
          quantifiedYear:
            defaultYear != null ? String(defaultYear) : ''
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setQbsPlanYearOptions([]);
          setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQbsPlanYears(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.serviceArea]);

  // Fetch activities when filters change
  useEffect(() => {
    const fetchActivities = async () => {
      if (!filters.serviceArea || !filters.quantifiedYear) {
        setActivities([]);
        setActivityRows([]);
        return;
      }

      setLoadingActivities(true);
      try {
        // Fetch quantified activities filtered by service area and contract year from server
        const filtered = await activityService.getQuantifiedActivitiesByServiceAreaAndYear(
          Number(filters.serviceArea),
          Number(filters.quantifiedYear)
        );

        setActivities(filtered);

        // Create rows with placeholder data
        const rows: ActivityRow[] = filtered.map(activity => ({
          activity,
          type: '', // Empty for now
          monthValues: Array(12).fill(''), // M1-M12 placeholder
          valueToDate: '',
          costsToDate: '',
          profit: '',
          grossMarginPercent: '',
          totalAccomplishment: '',
          costPerAccomplish: '',
          sched2Rate: activity.scheduled2Rate || '',
          avgActualValuePerAccomplishment: '',
          grossMarginPerAccomplishment: ''
        }));

        setActivityRows(rows);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setErrorMessage('Failed to load activities. Please try again.');
        setActivities([]);
        setActivityRows([]);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [filters.serviceArea, filters.quantifiedYear]);

  const handleGeneratePreview = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const data = await reportService.getQBSActualDetails({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      
      setMonthHeaders(data.monthHeaders || []);
      
      // Map API response to ActivityRow format
      const rows: ActivityRow[] = data.activityRows.map(row => {
        // Find matching activity from activities list
        const activity = activities.find(a => a.id === row.activityId) || {
          id: row.activityId,
          activityNumber: row.activityNumber,
          activityName: row.activityName,
          unitOfMeasure: '',
          scheduled2Rate: row.scheduled2Rate
        } as Activity;
        
        return {
          activity,
          type: row.type || '',
          monthValues: row.monthValues,
          valueToDate: row.valueToDate,
          costsToDate: row.costsToDate,
          profit: row.profit,
          grossMarginPercent: row.grossMarginPercent || 0,
          totalAccomplishment: row.totalAccomplishment,
          costPerAccomplish: row.costPerAccomplishment,
          sched2Rate: row.scheduled2Rate,
          avgActualValuePerAccomplishment: row.avgActualValuePerAccomplishment,
          grossMarginPerAccomplishment: row.grossMarginPerAccomplishment || 0
        };
      });
      
      setActivityRows(rows);
    } catch (error: any) {
      console.error('Error generating QBS Actual Details report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to generate QBS Actual Details report. Please try again later.');
      setActivityRows([]);
    } finally {
      setLoadingPreview(false);
    }
  };


  const formatValue = (value: number | string, isDollarAmount: boolean = true): string => {
    if (value === '' || value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'number') {
      // Change 0 to '-'
      if (value === 0) {
        return '-';
      }
      if (isDollarAmount) {
        // Round dollar amounts to nearest dollar
        return Math.round(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      }
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // Handle string values that might be '0' or '0.00'
    const numValue = parseFloat(String(value));
    if (!isNaN(numValue) && numValue === 0) {
      return '-';
    }
    return String(value);
  };

  const truncateActivityName = (text: string, maxLength: number = 50): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleExport = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.downloadQBSActualDetails({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      showSuccessToast('QBS Actual Details report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting QBS Actual Details report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to export QBS Actual Details report. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
        <ReportParametersCard
          onGenerate={handleGeneratePreview}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id.toString()}>
                    {area.name || area.code || 'Unknown'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
              <Form.Select
                size="sm"
                value={filters.quantifiedYear}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    quantifiedYear: e.target.value
                  }))
                }
                disabled={
                  !filters.serviceArea ||
                  loadingQbsPlanYears ||
                  qbsPlanYearOptions.length === 0
                }
              >
                <option value="">
                  {!filters.serviceArea
                    ? 'Select service area first'
                    : loadingQbsPlanYears
                      ? 'Loading years…'
                      : qbsPlanYearOptions.length === 0
                        ? 'No quantified plans'
                        : 'Select year'}
                </option>
                {qbsPlanYearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>
      
      {/* Report Data Section - fills the remaining pane height */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">
              Quantified Costing
              {activities.length > 0 && (
                <span className="tw-text-content-muted tw-ml-2" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                  ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
                </span>
              )}
            </h6>
            <div className="tw-flex tw-items-center tw-gap-2">
              {loadingActivities && (
                <span className="tw-text-content-muted" style={{ fontSize: '0.875rem' }}>
                  Loading activities...
                </span>
              )}
              {activityRows.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={!filters.serviceArea || !filters.quantifiedYear || loadingExport}
                >
                  <i className="bi bi-download tw-mr-1"></i>
                  {loadingExport ? 'Exporting...' : 'Export Report'}
                </Button>
              )}
            </div>
          </Card.Header>

          {/* Table Container - a single scroll region sized purely by flex (no viewport-relative
              maxHeight - that's what made the horizontal scrollbar land off-screen; see
              ReportPreviewTable.tsx for the same fix on the shared table). */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              overflow: 'auto',
              minHeight: 0,
              minWidth: 0,
              height: '100%'
            }}>
              {activityRows.length === 0 && !loadingActivities ? (
                <div className="tw-text-center tw-py-5 tw-text-content-muted">
                  {!filters.serviceArea || !filters.quantifiedYear 
                    ? 'Please select Service Area and Quantified Year to view activities'
                    : 'No activities found for the selected filters'}
                </div>
              ) : (
                <Table striped bordered hover style={{ marginBottom: 0, width: 'max-content', minWidth: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                    <tr>
                      <th style={{ 
                        maxWidth: '20%',
                        width: '20%',
                        backgroundColor: '#f8f9fa',
                        position: 'sticky',
                        left: 0,
                        zIndex: 12,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }} title="Activity">Activity</th>
                      {monthHeaders.length > 0 ? (
                        monthHeaders.map((month) => (
                          <th
                            key={month.label}
                            style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}
                            title={month.dateRange ? `${month.label} — ${month.dateRange}` : month.label}
                          >
                            <div>{month.label}</div>
                            {month.dateRange && (
                              <div style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#6c757d' }}>
                                {month.dateRange}
                              </div>
                            )}
                          </th>
                        ))
                      ) : (
                        Array.from({ length: 12 }, (_, i) => (
                          <th key={`M${i + 1}`} style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title={`M${i + 1}`}>
                            {`M${i + 1}`}
                          </th>
                        ))
                      )}
                      <th style={{ minWidth: '120px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Value to date">
                        Value to date
                      </th>
                      <th style={{ minWidth: '120px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Costs to date">
                        Costs to date
                      </th>
                      <th style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Gross Margin">
                        Gross Margin
                      </th>
                      <th style={{ minWidth: '80px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="GM%">
                        GM%
                      </th>
                      <th style={{ minWidth: '140px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Total Accomplish">
                        Total Accomplish
                      </th>
                      <th style={{ minWidth: '100px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Units">
                        Units
                      </th>
                      <th style={{ minWidth: '140px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Cost per Accomplish">
                        Cost per Accomplish
                      </th>
                      <th style={{ minWidth: '120px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Sched 2 Rate">
                        Sched 2 Rate
                      </th>
                      <th style={{ minWidth: '180px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Avg Actual Value per Accomplish">
                        Avg Actual Value per Accomplish
                      </th>
                      <th style={{ minWidth: '200px', textAlign: 'center', backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }} title="Gross Margin per Accomplishment">
                        Gross Margin per Accomplishment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityRows.map((row, index) => (
                      <tr key={row.activity.id || index}>
                        <td style={{ 
                          padding: '8px 12px',
                          position: 'sticky',
                          left: 0,
                          zIndex: 9,
                          backgroundColor: 'white',
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                          maxWidth: '20%',
                          width: '20%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {truncateActivityName(
                            row.activity.activityNumber && row.activity.activityName
                              ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
                              : row.activity.activityName || row.activity.activityNumber || 'Unknown Activity'
                          )}
                        </td>
                        {row.monthValues.map((value, monthIndex) => (
                          <td
                            key={monthIndex}
                            style={{
                              textAlign: 'right',
                              padding: '8px 12px'
                            }}
                          >
                            {formatValue(value)}
                          </td>
                        ))}
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.valueToDate)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.costsToDate)}
                        </td>
                        <td 
                          style={{ 
                            textAlign: 'right', 
                            padding: '8px 12px',
                            color: (() => {
                              const profitValue = typeof row.profit === 'number' ? row.profit : 
                                                 (typeof row.profit === 'string' && row.profit !== '' && row.profit !== '-') 
                                                   ? parseFloat(row.profit) : null;
                              if (profitValue === null || isNaN(profitValue)) return undefined;
                              return profitValue < 0 ? '#dc3545' : profitValue > 0 ? '#006400' : undefined; // red for negative, dark green for positive
                            })()
                          }}
                        >
                          {formatValue(row.profit)}
                        </td>
                        <td 
                          style={{ 
                            textAlign: 'right', 
                            padding: '8px 12px',
                            color: (() => {
                              const gmPercentValue = typeof row.grossMarginPercent === 'number' ? row.grossMarginPercent : 
                                                    (typeof row.grossMarginPercent === 'string' && row.grossMarginPercent !== '' && row.grossMarginPercent !== '-') 
                                                      ? parseFloat(row.grossMarginPercent) : null;
                              if (gmPercentValue === null || isNaN(gmPercentValue)) return undefined;
                              return gmPercentValue < 0 ? '#dc3545' : gmPercentValue > 0 ? '#006400' : undefined; // red for negative, dark green for positive
                            })()
                          }}
                        >
                          {typeof row.grossMarginPercent === 'number' 
                            ? `${row.grossMarginPercent.toFixed(2)}%`
                            : (() => {
                                const numValue = typeof row.grossMarginPercent === 'number' 
                                  ? row.grossMarginPercent 
                                  : parseFloat(String(row.grossMarginPercent));
                                if (isNaN(numValue)) return '-';
                                return `${numValue.toFixed(2)}%`;
                              })()}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.totalAccomplishment, false)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          {row.activity.unitOfMeasure || '-'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.costPerAccomplish)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.sched2Rate)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.avgActualValuePerAccomplishment)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.grossMarginPerAccomplishment)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type="success"
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default QBSActualDetailsReportView;